import { useState, useEffect, useRef } from "react";
import {
  useClaimSchemas,
  useCreateClaimSchema,
  useUpdateClaimSchema,
  useCreateClaimAttribute,
  useUpdateClaimAttribute,
  useDeleteClaimAttribute,
  type ClaimSchemaRow,
  type ClaimAttribute,
} from "@/admin/hooks/use-claim-schemas";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/shared/components/ui/select";
import { MasterDetail } from '@/admin/components/shared/master-detail';
import { AttributeDeleteDialog } from '@/admin/components/shared/attribute-delete-dialog';
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker';
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers';
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context';
import { Plus, Pencil, Trash2, GripVertical, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ── Constants ─────────────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'
const FIELD_TYPES = [
  "TEXT",
  "NUMBER",
  "SELECT",
  "BOOLEAN",
  "DATE",
  "TEXTAREA",
  "IMAGE",
  "VIDEO",
  "FILE",
  "DOCUMENT",
  "CURRENCY",
  "DECIMAL",
  "LONG_TEXT",
  "RICH_TEXT",
] as const;

type FieldType = (typeof FIELD_TYPES)[number];

// ── Local Components ──────────────────────────────────────

function GreenButton({
  children,
  onClick,
  disabled,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      variant="default"
      className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
    >
      {children}
    </Button>
  );
}

function IconButton({
  children,
  onClick,
  title,
  danger,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  danger?: boolean;
  'data-testid'?: string;
}) {
  return (
    <Button
      onClick={onClick}
      title={title}
      data-testid={dataTestId}
      variant="ghost"
      size="icon-xs"
      className={`h-6 w-6 flex items-center justify-center rounded-[4px] transition-all duration-150 cursor-pointer hover:bg-[var(--bg-subtle)] ${danger ? "text-[var(--red)]" : "text-[var(--ink-3)]"}`}
    >
      {children}
    </Button>
  );
}

function TypeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]"
    >
      {children}
    </span>
  );
}

// ── Helper ────────────────────────────────────────────────

function toCodeName(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

// ── Main Page Component ─────────────────────────────────

export function ClaimSchemasPage() {
  const { data: schemas, isLoading } = useClaimSchemas();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const createSchema = useCreateClaimSchema();

  const selectedSchema =
    (schemas ?? []).find((s) => s.uuid === selectedUuid) ?? null;

  async function handleCreate() {
    if (!newName.trim()) return;
    try {
      const created = await createSchema.mutateAsync({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
      });
      setSelectedUuid(created.uuid);
      setNewName("");
      setNewDescription("");
      setShowCreateDialog(false);
      toast.success("Schema created");
    } catch (err) {
      toastApiError(err, "Failed to create schema");
    }
  }

  return (
    <>
      <MasterDetail
        testId="claim-schemas"
        title="Claim Schemas"
        onAdd={() => setShowCreateDialog(true)}
        hasSelection={!!selectedUuid}
        selectedId={selectedUuid}
        visibleItemIds={(schemas ?? []).map((schema) => schema.uuid)}
        onSelectionChange={setSelectedUuid}
        onBack={() => setSelectedUuid(null)}
        list={
          <>
            {isLoading ? (
              <p
                className="text-[12px]"
                style={{ color: 'var(--ink-3)' }}
              >
                Loading...
              </p>
            ) : !(schemas ?? []).length ? (
              <p
                className="text-[12px]"
                style={{ color: 'var(--ink-3)' }}
              >
                No schemas yet
              </p>
            ) : (
              <div className="flex flex-col gap-0.5">
                {(schemas ?? []).map((schema) => {
                  const isActive = schema.uuid === selectedUuid
                  return (
                    <button
                      key={schema.uuid}
                      onClick={() => setSelectedUuid(schema.uuid)}
                      data-testid={`claim-schemas-item-${schema.uuid}`}
                      className="text-left px-3 py-2 rounded-[4px] text-[12px] transition-all duration-150 cursor-pointer"
                      style={{
                        color: isActive ? 'var(--green)' : 'var(--ink-2)',
                        background: isActive
                          ? 'var(--green-bg-subtle)'
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = 'var(--bg-subtle)'
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = 'transparent'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <FileText
                          className="h-3 w-3 shrink-0"
                          style={{ color: isActive ? 'var(--green)' : 'var(--ink-3)' }}
                        />
                        <span className="truncate flex-1">{schema.name}</span>
                      </div>
                      <div
                        className="mt-0.5 ml-5 text-[10px]"
                        style={{ color: 'var(--ink-3)' }}
                      >
                        {schema.attributes.length} attribute
                        {schema.attributes.length !== 1 ? 's' : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        }
        detail={
          selectedSchema ? (
            <SchemaDetail schema={selectedSchema} />
          ) : (
            <div className="flex justify-center pt-24">
              <p className="text-[13px] text-[var(--ink-3)]">
                Select a schema to view details
              </p>
            </div>
          )
        }
      />
      {/* Create dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Schema</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label className="clara-section-label mb-1.5 block">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Damage Claim"
                className="text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Description
              </Label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
                rows={3}
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
              />
            </div>
          </div>
          <DialogFooter>
            <GreenButton
              onClick={handleCreate}
              disabled={!newName.trim() || createSchema.isPending}
              data-testid="claim-schemas-dialog-submit"
            >
              Create
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Schema Detail ───────────────────────────────────────

function SchemaDetail({ schema }: { schema: ClaimSchemaRow }) {
  const updateSchema = useUpdateClaimSchema();

  const createAttribute = useCreateClaimAttribute();
  const updateAttribute = useUpdateClaimAttribute();
  const deleteAttribute = useDeleteClaimAttribute();

  // ── Local form state ──────────────────────────────────
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const [showAttrDialog, setShowAttrDialog] = useState(false);
  const [editingAttr, setEditingAttr] = useState<ClaimAttribute | null>(null);
  const [attrDisplayName, setAttrDisplayName] = useState("");
  const [attrCodeName, setAttrCodeName] = useState("");
  const [attrFieldType, setAttrFieldType] = useState<FieldType>("TEXT");
  const [attrRequired, setAttrRequired] = useState(false);
  const [attrVisibleToCustomer, setAttrVisibleToCustomer] = useState(true);
  const [attrOptions, setAttrOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [deletingAttr, setDeletingAttr] = useState<ClaimAttribute | null>(null);
  const resourceLookupProviders = useResourceLookupProviders();

  // Sync local state when schema changes
  const prevUuidRef = useRef<string | null>(null);
  useEffect(() => {
    if (schema && prevUuidRef.current !== schema.uuid) {
      prevUuidRef.current = schema.uuid;
       
      setEditName(schema.name);
      setEditDescription(schema.description || "");
      setIsDirty(false);
    }
  }, [schema]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortedAttributes = [...(schema.attributes ?? [])].sort(
    (a, b) => a.order - b.order,
  );

  // ── Schema save ──────────────────────────────────────

  async function handleSaveSchema() {
    try {
      await updateSchema.mutateAsync({
        uuid: schema.uuid,
        data: { name: editName.trim(), description: editDescription.trim() },
      });
      setIsDirty(false);
      toast.success("Schema updated");
    } catch (err) {
      toastApiError(err, "Failed to update schema");
    }
  }

  // ── Attribute dialogs ─────────────────────────────────

  function openCreateAttrDialog(name = "") {
    setEditingAttr(null);
    setAttrDisplayName(name);
    setAttrCodeName(toCodeName(name));
    setAttrFieldType("TEXT");
    setAttrRequired(false);
    setAttrVisibleToCustomer(true);
    setAttrOptions([]);
    setShowAttrDialog(true);
  }

  async function handleAddAttrPick(result: AttributeTemplatePickResult) {
    if (result.type === "create") {
      openCreateAttrDialog(result.name ?? "");
      return;
    }
    if (result.type === "existing") {
      const tpl: AttributeTemplateResult = result.template;
      try {
        await createAttribute.mutateAsync({
          schemaUuid: schema.uuid,
          data: {
            display_name: tpl.display_name,
            code_name: tpl.code_name,
            description: tpl.description ?? "",
            field_type: tpl.field_type,
            required: false,
            visibility: "PUBLIC",
            configuration_data: tpl.configuration_data ?? {},
            order: sortedAttributes.length,
          },
        });
        toast.success("Attribute created");
      } catch (err) {
        toastApiError(err, "Failed to create attribute");
      }
    }
  }

  function openEditAttrDialog(attr: ClaimAttribute) {
    setEditingAttr(attr);
    setAttrDisplayName(attr.display_name);
    setAttrCodeName(attr.code_name);
    setAttrFieldType(attr.field_type as FieldType);
    setAttrRequired(attr.required);
    setAttrVisibleToCustomer((attr.visibility ?? "PUBLIC") === "PUBLIC");
    const opts =
      (
        attr.configuration_data as {
          options?: { label: string; value: string }[];
        }
      )?.options ?? [];
    setAttrOptions(opts.map((o) => ({ label: o.label, value: o.value })));
    setShowAttrDialog(true);
  }

  async function handleSaveAttribute() {
    if (!attrDisplayName.trim()) return;

    const configData: Record<string, unknown> =
      attrFieldType === "SELECT" && attrOptions.length > 0
        ? {
            options: attrOptions.filter(
              (o) => o.label.trim() || o.value.trim(),
            ),
          }
        : {};

    const payload: Partial<ClaimAttribute> = {
      display_name: attrDisplayName.trim(),
      code_name: attrCodeName.trim() || toCodeName(attrDisplayName),
      description: "",
      field_type: attrFieldType,
      required: attrRequired,
      visibility: attrVisibleToCustomer ? "PUBLIC" : "INTERNAL",
      configuration_data: configData,
    };

    try {
      if (editingAttr) {
        await updateAttribute.mutateAsync({
          schemaUuid: schema.uuid,
          attrUuid: editingAttr.uuid,
          data: payload,
        });
        toast.success("Attribute updated");
      } else {
        payload.order = sortedAttributes.length;
        await createAttribute.mutateAsync({
          schemaUuid: schema.uuid,
          data: payload,
        });
        toast.success("Attribute created");
      }
      setShowAttrDialog(false);
    } catch (err) {
      toastApiError(
        err,
        editingAttr
          ? "Failed to update attribute"
          : "Failed to create attribute",
      );
    }
  }

  // ── Drag-and-drop reorder ─────────────────────────────

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedAttributes.findIndex((a) => a.uuid === active.id);
    const newIndex = sortedAttributes.findIndex((a) => a.uuid === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Build reordered array
    const reordered = [...sortedAttributes];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Update each attribute that changed order
    for (let i = 0; i < reordered.length; i++) {
      if (reordered[i].order !== i) {
        try {
          await updateAttribute.mutateAsync({
            schemaUuid: schema.uuid,
            attrUuid: reordered[i].uuid,
            data: { order: i },
          });
        } catch (err) {
          toastApiError(err, "Failed to reorder attributes");
          return;
        }
      }
    }
  }

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <div>
      {/* Schema name + description */}
      <div className="flex flex-col gap-3 mb-6">
        <Input
          value={editName}
          onChange={(e) => {
            setEditName(e.target.value);
            setIsDirty(true);
          }}
          className="border-none bg-transparent px-0 py-0 text-[16px]"
          placeholder="Schema name"
        />
        <textarea
          value={editDescription}
          onChange={(e) => {
            setEditDescription(e.target.value);
            setIsDirty(true);
          }}
          rows={2}
          placeholder="Description (optional)"
          className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
        />
        {isDirty && (
          <div>
            <GreenButton
              onClick={handleSaveSchema}
              disabled={updateSchema.isPending}
            >
              Save
            </GreenButton>
          </div>
        )}
      </div>
      {/* Attributes section */}
      <div className="border-t-[0.5px] border-t-[var(--rule)] pt-4">
        <div className="flex items-center justify-between mb-3">
          <span className="clara-section-label">Attributes</span>
          <AttributeTemplatePicker
            onPick={handleAddAttrPick}
            resetAfterPick
            asAddButton
            domain="claim"
            addButtonLabel="Add Attribute"
            data-testid="claim-schemas-add-attribute-picker"
          />
        </div>

        {sortedAttributes.length === 0 ? (
          <p className="py-4 text-[12px] text-[var(--ink-3)]">
            No attributes yet. Add one to define the schema fields.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedAttributes.map((a) => a.uuid)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-0">
                {sortedAttributes.map((attr) => (
                  <SortableAttributeRow
                    key={attr.uuid}
                    attr={attr}
                    onEdit={() => openEditAttrDialog(attr)}
                      onDelete={() => setDeletingAttr(attr)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
      {/* Attribute create/edit dialog */}
      <Dialog
        open={showAttrDialog}
        onOpenChange={setShowAttrDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAttr ? "Edit Attribute" : "Add Attribute"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Display Name
              </Label>
              <Input
                value={attrDisplayName}
                onChange={(e) => {
                  setAttrDisplayName(e.target.value);
                  if (!editingAttr) {
                    setAttrCodeName(toCodeName(e.target.value));
                  }
                }}
                placeholder="e.g. Damage Description"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Code Name
              </Label>
              <Input
                value={attrCodeName}
                onChange={(e) => setAttrCodeName(e.target.value)}
                placeholder="e.g. damage_description"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">
                Field Type
              </Label>
              <Select
                value={attrFieldType}
                onValueChange={(v) => setAttrFieldType(v as FieldType)}
              >
                <SelectTrigger
                  size="sm"
                  className="text-[11px] w-full"
                >
                  <SelectValue>{attrFieldType}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((ft) => (
                    <SelectItem key={ft} value={ft}>
                      {ft}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="attr-required"
                checked={attrRequired}
                onCheckedChange={(checked) => setAttrRequired(checked === true)}
              />
              <Label
                htmlFor="attr-required"
                className="text-[12px] text-[var(--ink-2)]"
              >
                Required
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="attr-visible-to-customer"
                checked={attrVisibleToCustomer}
                onCheckedChange={(checked) =>
                  setAttrVisibleToCustomer(checked === true)
                }
              />
              <Label
                htmlFor="attr-visible-to-customer"
                className="text-[12px] text-[var(--ink-2)]"
              >
                Visible to customer
              </Label>
              <span className="text-[10px] text-[var(--ink-3)]">
                Captured + visible to admins; uncheck to hide from the
                customer's claim record.
              </span>
            </div>
            {attrFieldType === "SELECT" && (
              <div>
                <Label className="clara-section-label mb-1.5 block">
                  Options
                </Label>
                <div className="flex flex-col gap-2">
                  {attrOptions.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={opt.label}
                        onChange={(e) => {
                          const updated = [...attrOptions];
                          updated[idx] = {
                            ...updated[idx],
                            label: e.target.value,
                            value:
                              updated[idx].value || toCodeName(e.target.value),
                          };
                          setAttrOptions(updated);
                        }}
                        placeholder="Label"
                        className="text-[12px] flex-1"
                      />
                      <Input
                        value={opt.value}
                        onChange={(e) => {
                          const updated = [...attrOptions];
                          updated[idx] = {
                            ...updated[idx],
                            value: e.target.value,
                          };
                          setAttrOptions(updated);
                        }}
                        placeholder="Value"
                        className="text-[12px] flex-1"
                      />
                      <Button
                        onClick={() =>
                          setAttrOptions(
                            attrOptions.filter((_, i) => i !== idx),
                          )
                        }
                        variant="ghost"
                        size="icon-xs"
                        className="h-6 w-6 flex items-center justify-center rounded-[4px] transition-all duration-150 cursor-pointer shrink-0 text-[var(--red)] hover:bg-[var(--bg-subtle)]"
                        title="Remove option"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() =>
                      setAttrOptions([...attrOptions, { label: "", value: "" }])
                    }
                    variant="outline"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[11px] transition-all duration-150 cursor-pointer self-start h-auto text-[var(--green)] border-[0.5px] border-solid border-[var(--rule)] hover:bg-[var(--bg-subtle)]"
                  >
                    <Plus className="h-3 w-3" />
                    Add option
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <GreenButton
              onClick={handleSaveAttribute}
              disabled={
                !attrDisplayName.trim() ||
                createAttribute.isPending ||
                updateAttribute.isPending
              }
              data-testid="claim-schemas-attr-dialog-submit"
            >
              {editingAttr ? "Save" : "Create"}
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AttributeDeleteDialog
        open={!!deletingAttr}
        onOpenChange={(next) => {
          if (!next) setDeletingAttr(null);
        }}
        attributeName={deletingAttr?.display_name}
        description="This removes the attribute from the claim schema. New claim submissions will no longer capture it."
        successMessage="Attribute deleted"
        isPending={deleteAttribute.isPending}
        onDelete={({ force }) =>
          deleteAttribute.mutateAsync({
            schemaUuid: schema.uuid,
            attrUuid: deletingAttr!.uuid,
            force,
          })
        }
      />
    </div>
    </ResourceLookupContextProvider>
  );
}

// ── Sortable Attribute Row ──────────────────────────────

function SortableAttributeRow({
  attr,
  onEdit,
  onDelete,
}: {
  attr: ClaimAttribute;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attr.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    borderBottom: "0.5px solid var(--rule)",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 py-2 px-1"
    >
      <Button
        {...attributes}
        {...listeners}
        variant="ghost"
        size="icon-xs"
        className="cursor-grab shrink-0 text-[var(--ink-3)] active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </Button>
      <span className="flex-1 truncate text-[13px] text-[var(--ink)]">
        {attr.display_name}
      </span>
      <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
        {attr.code_name}
      </span>
      <TypeBadge>{attr.field_type}</TypeBadge>
      {attr.required && (
        <span
          className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.06em] text-[var(--green)]"
        >
          Required
        </span>
      )}
      <div className="flex items-center gap-0.5 shrink-0">
        <IconButton onClick={onEdit} title="Edit attribute" data-testid={`claim-schemas-edit-${attr.uuid}`}>
          <Pencil className="h-3 w-3" />
        </IconButton>
        <IconButton onClick={onDelete} title="Delete attribute" danger data-testid={`claim-schemas-delete-${attr.uuid}`}>
          <Trash2 className="h-3 w-3" />
        </IconButton>
      </div>
    </div>
  );
}
